'use client'
import { Image } from 'components/Image'
import { Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink'
import { Button, Slider } from '@achieve/ascend'
import { useState, useEffect } from 'react'
import classNames from 'classnames'

export default function DebtSlider({
  mainImage,
  mainImageAlt,
  badgeImage,
  badgeImageAlt,
  title,
  subtitle,
  showImageTexts,
  imageTitle,
  imageSubtitle,
  linkHref,
  linkText,
  linkSubtext,
  phone,
  sliderProps,
  className,
}) {
  const [sliderValue, setSliderValue] = useState(sliderProps?.defaultValue)
  const [sliderLink, setSliderLink] = useState(linkHref)
  const dollarUSLocale = Intl.NumberFormat('en-US')

  useEffect(() => {
    setSliderLink(linkHref.replace('{sliderValue}', sliderValue))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderValue])

  function handleChange(e) {
    setSliderValue(e.target.value)
  }

  return (
    <div className={classNames('w-full', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 items-stretch ">
        <div className="h-[350px] relative">
          <div className="absolute inset-0">
            <Image
              src={mainImage}
              alt={mainImageAlt || ''}
              fill
              style={{ objectFit: 'cover' }}
              priority
            />
          </div>
          {badgeImage && (
            <div className="absolute top-0 left-0 translate-x-3 -translate-y-5 h-[140px] w-[130px]">
              <Image src={badgeImage} alt={badgeImageAlt || ''} width={140} height={130} priority />
            </div>
          )}
          {showImageTexts && (
            <div className="absolute top-0 left-0 w-full text-left px-8 py-4 flex flex-col gap-4">
              <Typography content={imageTitle} className="headingMd text-white" />
              <Typography content={imageSubtitle} className="bodyMd text-white" />
            </div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center gap-4 py-4 px-8">
          <Typography content={title} className="headingMd text-center" />
          <Typography content={subtitle} className="displayXs text-center" />
          <div className="w-full px-10">
            <Typography
              content={`$${dollarUSLocale.format(sliderValue)}`}
              className="headingMd text-center font-bold"
            />
            <Slider
              aria-label="dollar-amount-selection-slider"
              className={'w-full'}
              classes={{
                mark: 'hidden',
              }}
              data-testid="product-hero-slider-input"
              value={sliderValue}
              step={sliderProps?.step}
              min={sliderProps?.minValue}
              max={sliderProps?.maxValue}
              marks={[
                {
                  value: sliderProps?.minValue,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(sliderProps?.minValue)}`}
                      variant="bodySm"
                      fontWeight="medium"
                      className={'ml-[50px] text-gray-600'}
                    />
                  ),
                },
                {
                  value: sliderProps?.maxValue,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(sliderProps?.maxValue)}${
                        sliderProps?.maxPlus ? '+' : ''
                      }`}
                      variant="bodySm"
                      fontWeight="medium"
                      className={'-ml-20 text-gray-600'}
                    />
                  ),
                },
              ]}
              onChange={handleChange}
            />
          </div>
          {linkHref && (
            <div className="w-full px-10">
              <AchieveLink href={sliderLink} target="_blank" rel="noopener noreferrer">
                <Button variant="contained" color="primary" className="w-full">
                  <Typography content={linkText} className="displayXs text-center" />
                </Button>
              </AchieveLink>
            </div>
          )}
          {phone && (
            <div className="bodySm text-center w-full flex lg:flex-row gap-1 items-center justify-center">
              <Typography
                content="Or speak to a debt consultant"
                className="bodySm text-blue-400"
              />
              <AchieveLink href={`tel:${phone}`} target="_blank" rel="noopener noreferrer">
                <Typography content={phone} className="bodySm font-bold! text-blue-500" />
              </AchieveLink>
            </div>
          )}
          {linkSubtext && (
            <div className="bodySm text-center">
              <Typography content={linkSubtext} className="bodySm text-center" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { DebtSlider }
